import {
  type Expression,
  TTypeApplication,
  TTypeConstructor,
  ERecord,
  EUnion,
} from '@packages/systems/wfdl-ast';
import {
  pluginName,
  typeName,
  qualify,
  type Qualified,
} from '@packages/systems/wfdl-Qualified';
import {
  Expressions,
  Plugins,
  Type,
  Variable,
  MetaAttributes,
} from '@packages/systems/wfdl';
import {_$, call1} from '@packages/utilities/wfdl-helpers';

export type BooleanTag = {
  tag: 'True' | 'False';
  val: Record<any, any>;
};

const booleanPluginName = pluginName('Boolean');
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const name = <nameType extends unknown>(n: nameType): Qualified<nameType> =>
  qualify(booleanPluginName, n);

export const TBoolean = TTypeConstructor(
  qualify(pluginName('Boolean'), typeName('Boolean'))
);

export const TRUE = {tag: 'True', val: {}} as const;
export const FALSE = {tag: 'False', val: {}} as const;

export function toBooleanTag(bool: boolean): BooleanTag {
  return bool ? TRUE : FALSE;
}

const tyPredicate = TTypeConstructor(name(typeName('Predicate')));

export const TPredicate = (arg: Type): Type =>
  TTypeApplication({
    con: tyPredicate,
    arg,
  });

export const predicate = call1(_$('Boolean', 'Predicate'));
export const runPredicate = call1(_$('Boolean', 'Run Predicate'));

export const Every = Variable.qualified(
  qualify(Plugins.name('Boolean'), typeName('Every'))
);
export const Some = Variable.qualified(
  qualify(Plugins.name('Boolean'), typeName('Some'))
);

export const True = _$('Boolean', 'True');
export const False = _$('Boolean', 'False');

export function toBooleanDefaultValue(
  visibilityConditionsDefaultValue: unknown
) {
  if (
    Array.isArray(visibilityConditionsDefaultValue) &&
    visibilityConditionsDefaultValue.length === 1 &&
    visibilityConditionsDefaultValue[0] === false
  ) {
    return toBooleanTag(false);
  }
  return toBooleanTag(true);
}

export function toBooleanDefaultValueExpression(
  visibilityConditionsExpr: Expression
): Expression {
  const list = Expressions.getList(visibilityConditionsExpr);
  if (list?.[0] && Expressions.getBoolean(list[0]) === false) {
    return EUnion({tag: 'False', value: ERecord({})});
  }
  return EUnion({tag: 'True', value: ERecord({})});
}

export function parseLabelsFromMeta(meta: MetaAttributes | null): {
  trueLabel: string | null;
  falseLabel: string | null;
} {
  if (!meta) return {trueLabel: null, falseLabel: null};
  return {
    trueLabel:
      meta.trueLabel && typeof meta.trueLabel === 'string'
        ? meta.trueLabel
        : null,
    falseLabel:
      meta.falseLabel && typeof meta.falseLabel === 'string'
        ? meta.falseLabel
        : null,
  };
}
